import { ConductorError, ErrorType } from "@sourceacademy/conductor/common";
import { IRunnerPlugin } from "@sourceacademy/conductor/runner";
import { Context } from "./context";
import { ErrorValue } from "./stash";

export type WritableContext<T> = {
  stream: WritableStream<T>;
  writer: WritableStreamDefaultWriter<T>;
};

export type ReadableContext<T> = {
  stream: ReadableStream<T>;
  reader: ReadableStreamDefaultReader<T>;
};

export function createOutputStream(conductor: IRunnerPlugin): WritableContext<string> {
  const stream = new WritableStream<string>({
    write: chunk => {
      conductor.sendOutput(chunk);
    },
  });
  const writer = stream.getWriter();
  return { stream, writer };
}

export function createBufferedOutputStream(): {
  context: WritableContext<string>;
  flush: (conductor: IRunnerPlugin) => void;
} {
  let buffer = "";
  const stream = new WritableStream<string>({
    write: chunk => {
      buffer += chunk;
    },
  });
  const writer = stream.getWriter();
  return {
    context: { stream, writer },
    flush: (conductor: IRunnerPlugin) => {
      if (buffer !== "") {
        conductor.sendOutput(buffer);
        buffer = "";
      }
    },
  };
}

export function createErrorStream(conductor: IRunnerPlugin): WritableContext<ConductorError> {
  const stream = new WritableStream<ConductorError>({
    write: chunk => {
      conductor.sendError(chunk);
    },
  });

  const writer = stream.getWriter();
  return { stream, writer };
}

export type InputStreamContext = ReadableContext<string> & {
  /** Sets the prompt to show the user on the next `requestInput()` round-trip, if any. */
  setNextPrompt: (prompt?: string) => void;
};

export const createInputStream = (conductor: IRunnerPlugin): InputStreamContext => {
  let pendingPrompt: string | undefined;
  const stream = new ReadableStream<string>(
    {
      async pull(controller) {
        const prompt = pendingPrompt;
        pendingPrompt = undefined;
        const input = await conductor.requestInput(prompt);
        controller.enqueue(input);
      },
    },
    // Default highWaterMark of 1 makes the stream eagerly pull() as soon as it's constructed
    // (before any input() call sets a prompt), firing a phantom requestInput(undefined) and
    // leaving the real prompt to be used one call late. highWaterMark: 0 makes pull() only run
    // in direct response to reader.read().
    { highWaterMark: 0 },
  );
  const reader = stream.getReader();
  return {
    stream,
    reader,
    setNextPrompt: prompt => {
      pendingPrompt = prompt;
    },
  };
};

export const displayError = async (
  context: Context,
  error: unknown,
  type: ErrorType,
): Promise<ErrorValue> => {
  const name =
    typeof error === "object" && error !== null && "name" in error && typeof error.name === "string"
      ? error.name
      : "Error";
  const message =
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
      ? error.message
      : String(error);
  if (context.streams.initialised) {
    await context.streams.stderr.writer.write({ name, message, errorType: type });
  }
  return { type: "error", message: message };
};

export const displayOutput = async (context: Context, output: string) => {
  if (context.streams.initialised) {
    await context.streams.stdout.writer.write(output);
  }
};

export const receiveInput = async (context: Context, prompt?: string): Promise<string> => {
  if (context.streams.initialised) {
    // Force out any output already buffered (e.g. earlier print()s in this chunk) so the user
    // sees it before we block waiting for their answer, rather than it sitting unsent until the
    // whole chunk finishes.
    context.streams.flushStdout?.();
    context.streams.stdin.setNextPrompt(prompt);
    const reader = context.streams.stdin.reader;
    const { value } = await reader.read();
    return value ?? "";
  }
  return "";
};

export const destroyStreams = async (context: Context) => {
  if (context.streams.initialised) {
    context.streams.stdout.writer.releaseLock();
    context.streams.stderr.writer.releaseLock();
    context.streams.stdin.reader.releaseLock();

    await Promise.allSettled([
      context.streams.stdout.stream.close(),
      context.streams.stderr.stream.close(),
      context.streams.stdin.stream.cancel(),
    ]);
  }
  context.streams = { initialised: false };
};
